import React, { useState } from "react";
import {
  Button,
  ButtonVariant,
  PRODUCT_LABELS,
  Text,
  TextFieldVariant,
  Typeahead,
} from "@merge-api/merge-javascript-shared";
import type { ProductKey } from "@merge-api/merge-javascript-shared";
import { ChevronDown } from "lucide-react";
import { User, UserType } from "../../../../../../../models/Entities";
import { fetchCurrentUser } from "../../../../../../../api-client/APIClient";
import { showSuccessToast, showErrorToast } from "../../../../../../shared/Toasts";
import useAppContext from "../../../../../../context/useAppContext";
import changeAllowedProducts from "../utils/changeAllowedProducts";
import useProductsDraft from "../hooks/useProductsDraft";

interface Props {
  user: User;
  options: ProductKey[];
  onSaved: (savedUser: User) => void;
}

const ProductsCell = ({ user, options, onSaved }: Props) => {
  // state
  const [products, setProducts] = useState<ProductKey[]>(user.allowed_products ?? []);
  const [isSaving, setIsSaving] = useState(false);

  // hooks
  const { user: meUser, setUser } = useAppContext();
  const isSelf = user.id === meUser.id;
  const {
    draft,
    shownProducts,
    typeaheadRef,
    startEditing,
    isOptionDisabled,
    onDraftChange,
    onTypeaheadBlur,
    onTypeaheadKeyDown,
  } = useProductsDraft({
    products,
    options,
    lockedProduct: isSelf && products.includes("unified") ? "unified" : null,
    onFinish: (nextProducts) => saveProducts(nextProducts),
  });

  // derived state
  const canModifyUser = meUser.type === UserType.admin_with_billing;
  const label =
    shownProducts.length === options.length
      ? "All"
      : shownProducts.map((product) => PRODUCT_LABELS[product]).join(", ") || "None";

  // event handlers
  const saveProducts = (nextProducts: ProductKey[]) => {
    setProducts(nextProducts);
    setIsSaving(true);
    changeAllowedProducts({
      userId: user.id,
      allowedProducts: nextProducts,
      onSuccess: (savedUser) => {
        setIsSaving(false);
        onSaved(savedUser);
        if (isSelf) {
          fetchCurrentUser(setUser);
        }
        showSuccessToast(`Updated ${user.name}'s product access!`);
      },
      onError: () => {
        setIsSaving(false);
        setProducts(products);
        showErrorToast(`Failed to update ${user.name}'s product access.`);
      },
    });
  };

  // render
  if (!canModifyUser) {
    return (
      <td className="items-start">
        <Text>{label}</Text>
      </td>
    );
  }

  return (
    <td className="items-start">
      <div className="-ml-3">
        {draft !== null ? (
          <div ref={typeaheadRef} onBlur={onTypeaheadBlur} onKeyDownCapture={onTypeaheadKeyDown}>
            <Typeahead
              multiple
              value={draft}
              options={options}
              onChange={(_, value) => onDraftChange(value)}
              getOptionLabel={(product) => PRODUCT_LABELS[product]}
              getOptionDisabled={isOptionDisabled}
              renderBadge={({ tag, dataTagIndex }) => (
                <Text>{dataTagIndex < draft.length - 1 ? `${tag},` : tag}</Text>
              )}
              borderVariant={TextFieldVariant.Inlined}
              disableCloseOnSelect
              disableClearable
            />
          </div>
        ) : (
          <Button
            variant={ButtonVariant.TextBlack}
            size="sm"
            rightIcon={<ChevronDown size={12} />}
            loading={isSaving}
            onClick={startEditing}
          >
            <Text>{label}</Text>
          </Button>
        )}
      </div>
    </td>
  );
};

export default ProductsCell;
